import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { colors, spacing, borderRadius, Icon, Alert } from '@tipsy/design-system';
import { cardsApi } from '../api';
import type { WalletStackParamList } from '../navigation/MainNavigator';

type NavigationProp = NativeStackNavigationProp<WalletStackParamList, 'CardSetup'>;

type Step = 'loading' | 'personal' | 'address' | 'currency' | 'creating';

const CardSetupScreen = () => {
  const navigation = useNavigation<NavigationProp>();
  const [step, setStep] = useState<Step>('loading');
  const [hasCardholder, setHasCardholder] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkCardholder = async () => {
      try {
        const cardholder = await cardsApi.getCardholder();
        if (cardholder) {
          setHasCardholder(true);
          setStep('currency');
        } else {
          setStep('personal');
        }
      } catch {
        setStep('personal');
      }
    };
    checkCardholder();
  }, []);

  // Personal info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Address
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // Currency (only USD available for now)
  const [currency, setCurrency] = useState<'usd'>('usd');

  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validation checks (no alerts, just boolean)
  const isPersonalInfoValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    email.includes('@') &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth.trim());

  const isAddressValid =
    line1.trim().length > 0 && city.trim().length > 0 && postalCode.trim().length > 0 && termsAccepted;

  const isCurrentStepValid =
    (step === 'personal' && isPersonalInfoValid) ||
    (step === 'address' && isAddressValid) ||
    step === 'currency';

  const handleNext = () => {
    if (step === 'personal' && isPersonalInfoValid) {
      setStep('address');
    } else if (step === 'address' && isAddressValid) {
      setStep('currency');
    }
  };

  const handleBack = () => {
    if (step === 'address') {
      setStep('personal');
    } else if (step === 'currency') {
      setStep('address');
    }
  };

  const handleCreateCard = async () => {
    try {
      setIsLoading(true);
      setStep('creating');

      // Create cardholder if not exists (only for new users going through full flow)
      if (!hasCardholder) {
        await cardsApi.createCardholder({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          dateOfBirth: dateOfBirth.trim(),
          phoneNumber: phoneNumber.trim() || undefined,
          billingAddress: {
            line1: line1.trim(),
            line2: line2.trim() || undefined,
            city: city.trim(),
            state: state.trim() || undefined,
            postalCode: postalCode.trim(),
          },
          termsAccepted: true,
        });
      }

      // Create virtual card
      const card = await cardsApi.createCard({ currency });

      Alert.alert('Success', 'Your virtual card has been created!', [
        {
          text: 'View Card',
          onPress: () => navigation.replace('VirtualCard', { cardId: card.id }),
        },
      ]);
    } catch (error: unknown) {
      setStep('currency');
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message || 'Failed to create card. Please try again.';
      Alert.alert('Error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => {
    // If cardholder exists, only show currency step
    if (hasCardholder) {
      return null;
    }

    const steps = ['Personal', 'Address', 'Currency'];
    const currentIndex = step === 'personal' ? 0 : step === 'address' ? 1 : 2;

    return (
      <View style={styles.stepIndicator}>
        {steps.map((s, index) => (
          <View key={s} style={styles.stepContainer}>
            <View style={[styles.stepCircle, index <= currentIndex && styles.stepCircleActive]}>
              <Text style={[styles.stepNumber, index <= currentIndex && styles.stepNumberActive]}>
                {index + 1}
              </Text>
            </View>
            <Text style={[styles.stepLabel, index <= currentIndex && styles.stepLabelActive]}>
              {s}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderPersonalStep = () => (
    <>
      <Text style={styles.stepTitle}>Personal Information</Text>
      <Text style={styles.stepDescription}>
        Enter your personal details as they appear on your ID.
      </Text>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>First Name</Text>
        <TextInput
          style={styles.input}
          value={firstName}
          onChangeText={setFirstName}
          placeholder="John"
          placeholderTextColor={colors.text.tertiary}
          autoCapitalize="words"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Last Name</Text>
        <TextInput
          style={styles.input}
          value={lastName}
          onChangeText={setLastName}
          placeholder="Doe"
          placeholderTextColor={colors.text.tertiary}
          autoCapitalize="words"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Email</Text>
        <TextInput
          style={styles.input}
          value={email}
          onChangeText={setEmail}
          placeholder="john@example.com"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="email-address"
          autoCapitalize="none"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Date of Birth</Text>
        <TextInput
          style={styles.input}
          value={dateOfBirth}
          onChangeText={setDateOfBirth}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="numbers-and-punctuation"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Phone Number (Optional)</Text>
        <TextInput
          style={styles.input}
          value={phoneNumber}
          onChangeText={setPhoneNumber}
          placeholder="+1 415 555 1234"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="phone-pad"
        />
        <Text style={styles.inputHint}>
          E.164 format: +1 for US, +44 for UK, +49 for DE. Required for UK/EU.
        </Text>
      </View>
    </>
  );

  const renderAddressStep = () => (
    <>
      <Text style={styles.stepTitle}>Billing Address</Text>
      <Text style={styles.stepDescription}>Enter your billing address for the card.</Text>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Street Address</Text>
        <TextInput
          style={styles.input}
          value={line1}
          onChangeText={setLine1}
          placeholder="123 Main St"
          placeholderTextColor={colors.text.tertiary}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Apt, Suite, etc. (Optional)</Text>
        <TextInput
          style={styles.input}
          value={line2}
          onChangeText={setLine2}
          placeholder="Apt 4B"
          placeholderTextColor={colors.text.tertiary}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>City</Text>
        <TextInput
          style={styles.input}
          value={city}
          onChangeText={setCity}
          placeholder="San Francisco"
          placeholderTextColor={colors.text.tertiary}
        />
      </View>

      <View style={styles.inputRow}>
        <View style={[styles.inputGroup, styles.inputHalf]}>
          <Text style={styles.inputLabel}>State/Province</Text>
          <TextInput
            style={styles.input}
            value={state}
            onChangeText={setState}
            placeholder="CA"
            placeholderTextColor={colors.text.tertiary}
          />
        </View>

        <View style={[styles.inputGroup, styles.inputHalf]}>
          <Text style={styles.inputLabel}>Postal Code</Text>
          <TextInput
            style={styles.input}
            value={postalCode}
            onChangeText={setPostalCode}
            placeholder="94105"
            placeholderTextColor={colors.text.tertiary}
          />
        </View>
      </View>

      <TouchableOpacity
        style={styles.termsContainer}
        onPress={() => setTermsAccepted(!termsAccepted)}
        activeOpacity={0.7}
      >
        <View style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}>
          {termsAccepted && <Icon name="check" size={16} color={colors.text.inverse} />}
        </View>
        <Text style={styles.termsText}>
          I agree to the <Text style={styles.termsLink}>Terms of Service</Text> and{' '}
          <Text style={styles.termsLink}>Privacy Policy</Text> for Stripe Issuing
        </Text>
      </TouchableOpacity>
    </>
  );

  const renderCurrencyStep = () => (
    <>
      <Text style={styles.stepTitle}>{hasCardholder ? 'Add New Card' : 'Card Currency'}</Text>
      <Text style={styles.stepDescription}>
        Select the currency for your {hasCardholder ? 'new ' : ''}virtual card.
      </Text>

      <View style={styles.currencyOptions}>
        {[{ value: 'usd', label: 'USD', symbol: '$', name: 'US Dollar' }].map((option) => (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.currencyOption,
              currency === option.value && styles.currencyOptionSelected,
            ]}
            onPress={() => setCurrency(option.value as 'usd')}
          >
            <Text style={styles.currencySymbol}>{option.symbol}</Text>
            <Text style={styles.currencyLabel}>{option.label}</Text>
            <Text style={styles.currencyName}>{option.name}</Text>
            {currency === option.value && (
              <View style={styles.currencyCheck}>
                <Icon name="check" size={16} color={colors.text.inverse} />
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
    </>
  );

  if (step === 'loading' || step === 'creating') {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.creatingContainer}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
          <Text style={styles.creatingText}>
            {step === 'loading' ? 'Loading...' : 'Creating your virtual card...'}
          </Text>
          {step === 'creating' && (
            <Text style={styles.creatingSubtext}>This may take a few seconds.</Text>
          )}
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {renderStepIndicator()}

          {step === 'personal' && renderPersonalStep()}
          {step === 'address' && renderAddressStep()}
          {step === 'currency' && renderCurrencyStep()}
        </ScrollView>

        <View style={styles.footer}>
          {step !== 'personal' && !hasCardholder && (
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={handleBack}
              disabled={isLoading}
            >
              <Text style={styles.secondaryButtonText}>Back</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[
              styles.primaryButton,
              step === 'personal' && styles.primaryButtonFull,
              (!isCurrentStepValid || isLoading) && styles.primaryButtonDisabled,
            ]}
            onPress={step === 'currency' ? handleCreateCard : handleNext}
            disabled={!isCurrentStepValid || isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color={colors.text.inverse} />
            ) : (
              <Text style={styles.primaryButtonText}>
                {step === 'currency' ? 'Create Card' : 'Continue'}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.base,
    paddingTop: spacing.lg,
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: spacing.xl,
    gap: spacing.xl,
  },
  stepContainer: {
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.border.light,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  stepCircleActive: {
    backgroundColor: colors.interactive.primary,
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.tertiary,
  },
  stepNumberActive: {
    color: colors.text.inverse,
  },
  stepLabel: {
    fontSize: 12,
    color: colors.text.tertiary,
  },
  stepLabelActive: {
    color: colors.text.primary,
    fontWeight: '500',
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  stepDescription: {
    fontSize: 16,
    color: colors.text.secondary,
    marginBottom: spacing.xl,
    lineHeight: 24,
  },
  inputGroup: {
    marginBottom: spacing.lg,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  input: {
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    fontSize: 16,
    color: colors.text.primary,
    borderWidth: 1,
    borderColor: colors.border.light,
  },
  inputHint: {
    fontSize: 12,
    color: colors.text.tertiary,
    marginTop: spacing.xs,
  },
  inputRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  inputHalf: {
    flex: 1,
  },
  currencyOptions: {
    gap: spacing.md,
  },
  currencyOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    borderWidth: 2,
    borderColor: colors.border.light,
  },
  currencyOptionSelected: {
    borderColor: colors.interactive.primary,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text.primary,
    width: 40,
  },
  currencyLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
    marginRight: spacing.sm,
  },
  currencyName: {
    fontSize: 14,
    color: colors.text.secondary,
    flex: 1,
  },
  currencyCheck: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.interactive.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    flexDirection: 'row',
    padding: spacing.base,
    gap: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border.light,
    backgroundColor: colors.background.primary,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: colors.interactive.primary,
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  primaryButtonFull: {
    flex: 1,
  },
  primaryButtonDisabled: {
    opacity: 0.5,
  },
  primaryButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.text.inverse,
  },
  secondaryButton: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    justifyContent: 'center',
  },
  secondaryButtonText: {
    fontSize: 17,
    fontWeight: '500',
    color: colors.interactive.primary,
  },
  creatingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
  },
  creatingText: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: spacing.xl,
  },
  creatingSubtext: {
    fontSize: 16,
    color: colors.text.secondary,
    marginTop: spacing.sm,
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.lg,
    marginBottom: spacing.md,
    paddingVertical: spacing.sm,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: borderRadius.sm,
    borderWidth: 2,
    borderColor: colors.border.medium,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: colors.interactive.primary,
    borderColor: colors.interactive.primary,
  },
  termsText: {
    flex: 1,
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 22,
  },
  termsLink: {
    color: colors.text.link,
    fontWeight: '500',
  },
});

export default CardSetupScreen;
